// ✅ src/pages/VehicleV2/ProgressDetails.jsx  (FULL FILE)
// ASPX: ProgressDetails.aspx
//
// Backend mount (expected):
//   app.use("/api/progressDetails", progressDetailsRouter);
//
// Expected APIs (you can match to your SP flow):
//   GET  https://nissi-india.com/api/progressDetails/init?branchId=8&branchType=HO&vehType=O
//        -> { success, branches:[{label,value}], years:[...], months:[...], userLock:{branch,vehCode,vehName,regNo,disableAllVehicleFields} }
//
//   GET  https://nissi-india.com/api/progressDetails/list?vehType=O&branchType=HO&branch=8&regNo=&vehicleName=&vehicleCode=&
//        fromDate=2026-02-01&toDate=2026-02-06&month=February&year=2026&page=1&pageSize=10
//        -> { success, rows:[...], total }
//
// Optional (autocomplete - if you want like ASPX AutoCompleteExtender):
//   GET  https://nissi-india.com/api/progressDetails/suggest?field=vehicleCode&q=AB&branch=8&vehType=O
//   GET  https://nissi-india.com/api/progressDetails/suggest?field=vehicleName&q=EXC&branch=8&vehType=O
//   GET  https://nissi-india.com/api/progressDetails/suggest?field=regNo&q=TN&branch=8&vehType=O
//
// UI/Flow matched to ASPX:
// - Branch dropdown (usually disabled by HO/Branch rules; init can lock it)
// - Vehicle RegNo / Code / Name inputs
// - Month + Year dropdown
// - FromDate / ToDate
// - Search + Print buttons
// - Grid with Status as clickable (opens ProgressStatus in popup/new tab)
// - Pagination
// - Highlight rows (backend can send: isHighlight=1 OR highlight=true)
//
// NOTE:
// - Keep dates as yyyy-mm-dd in UI; backend can convert to dd/MM/yyyy if SP needs.
// - If you already have print/status routes, just adjust the window.open URLs below.

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/progressDetails";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

// Convert yyyy-mm-dd => dd/MM/yyyy (only if your SP requires)
// const toDMY = (v) => {
//   const s = toStr(v);
//   if (!s) return "";
//   if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
//     const [yyyy, mm, dd] = s.split("-");
//     return `${dd}/${mm}/${yyyy}`;
//   }
//   return s;
// };

const normalizeOptionList = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };
    const label = x.label ?? x.fld_branchname ?? x.value ?? x.text ?? "";
    const value = x.value ?? x.fld_branchid ?? x.id ?? x.label ?? "";
    return { label: String(label), value: String(value) };
  });
};

export default function ProgressDetails() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Match your session-driven ctx (replace with your real session values if you have)
  const [userCtx] = useState(() => ({
    branchId: Number(sessionStorage.getItem("branchid")) || 8,
    branchType: toStr(sessionStorage.getItem("branchtype")) || "HO", // "HO" or "BRANCH"
    loginUserId: Number(sessionStorage.getItem("loginuserid")) || 37,
    vehType: toStr(sessionStorage.getItem("prvehtype")) || "O", // ASPX: Session["prvehtype"]
    // Optional locked values (ASPX uses prvehcode/prvehname/prvehregno/prbranchid etc.)
    prvehcode: toStr(sessionStorage.getItem("prvehcode")) || "",
    prvehname: toStr(sessionStorage.getItem("prvehname")) || "",
    prvehregno: toStr(sessionStorage.getItem("prvehregno")) || "",
    prbranchid: toStr(sessionStorage.getItem("prbranchid")) || "",
    prmonth: toStr(sessionStorage.getItem("prmonth")) || "",
    pryear: toStr(sessionStorage.getItem("pryear")) || "",
    prvehiclecode: toStr(sessionStorage.getItem("prvehiclecode")) || "",
  }));

  const [showFilters, setShowFilters] = useState(true);

  // Init dropdowns (branch + years + months)
  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    months: [{ label: "Select", value: "Select" }],
    years: [{ label: "Select", value: "Select" }],
  });

  // Backend may return "lock" behavior like ASPX fillbranch() disables fields
  const [lock, setLock] = useState({
    disableBranch: true,
    disableVehicleFields: true,
  });

  // Filters (Month/Year logic: if Select, ASPX uses Session["prmonth"] and ["pryear"])
  const [filters, setFilters] = useState({
    branch: "Select",
    regNo: "",
    vehicleCode: "",
    vehicleName: "",
    month: "Select",
    year: "Select",
    fromDate: "",
    toDate: "",
  });

  // Grid
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  // --- Autocomplete (optional) ---
  const [suggest, setSuggest] = useState({
    regNo: [],
    vehicleCode: [],
    vehicleName: [],
  });

  const fetchSuggest = async (field, q) => {
    const query = toStr(q);
    if (query.length < 1) return [];
    try {
      const { data } = await axios.get(`${API_BASE}/suggest`, {
        params: {
          field, // regNo | vehicleCode | vehicleName
          q: query,
          branch: filters.branch,
          branchType: userCtx.branchType,
          branchId: userCtx.branchId,
          vehType: userCtx.vehType,
        },
      });
      if (!data?.success) return [];
      const arr = Array.isArray(data.items) ? data.items : [];
      return arr.map((x) => String(x));
    } catch {
      return [];
    }
  };

  const loadInit = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: userCtx.branchId,
          branchType: userCtx.branchType,
          vehType: userCtx.vehType,
        },
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      const branches = normalizeOptionList(data.branches);
      const months = normalizeOptionList(data.months);
      const years = normalizeOptionList(data.years);

      setDd({
        branches: branches.length ? branches : [{ label: "Select", value: "Select" }],
        months: months.length ? months : [
          { label: "Select", value: "Select" },
          { label: "January", value: "January" },
          { label: "February", value: "February" },
          { label: "March", value: "March" },
          { label: "April", value: "April" },
          { label: "May", value: "May" },
          { label: "June", value: "June" },
          { label: "July", value: "July" },
          { label: "August", value: "August" },
          { label: "September", value: "September" },
          { label: "October", value: "October" },
          { label: "November", value: "November" },
          { label: "December", value: "December" },
        ],
        years: years.length ? years : [{ label: "Select", value: "Select" }],
      });

      // Apply ASPX-like locks + prefilled values (from session / init userLock)
      const userLock = data.userLock || {};
      const lockedBranch =
        userLock.branch ??
        (userCtx.prbranchid && userCtx.prbranchid !== "Select"
          ? userCtx.prbranchid
          : String(userCtx.branchId));

      const lockedVehicleCode = userLock.vehCode ?? userCtx.prvehcode ?? "";
      const lockedVehicleName = userLock.vehName ?? userCtx.prvehname ?? "";
      const lockedRegNo = userLock.regNo ?? userCtx.prvehregno ?? "";

      setLock({
        disableBranch: userLock.disableBranch ?? true, // ASPX disables branch in both HO and branch cases
        disableVehicleFields: userLock.disableAllVehicleFields ?? true, // ASPX disables 3 vehicle inputs after fillbranch
      });

      setFilters((p) => ({
        ...p,
        branch: lockedBranch || p.branch,
        vehicleCode: lockedVehicleCode || p.vehicleCode,
        vehicleName: lockedVehicleName || p.vehicleName,
        regNo: lockedRegNo || p.regNo,
        // Month/year from session if present
        month: userCtx.prmonth ? userCtx.prmonth : p.month,
        year: userCtx.pryear ? userCtx.pryear : p.year,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load init data");
    }
  };

  const loadList = async (overrides = {}) => {
    try {
      const f = { ...filters, ...overrides };

      // ASPX: if month/year select => use Session prmonth/pryear
      const month = !isSelect(f.month) ? f.month : (userCtx.prmonth || "");
      const year = !isSelect(f.year) ? f.year : (userCtx.pryear || "");

      const params = {
        vehType: userCtx.vehType,
        branchType: userCtx.branchType,
        branchId: userCtx.branchId,
        loginUserId: userCtx.loginUserId,

        branch: isSelect(f.branch) ? "Select" : f.branch,
        regNo: toStr(f.regNo),
        vehicleName: toStr(f.vehicleName),
        vehicleCode: toStr(f.vehicleCode),

        fromDate: toStr(f.fromDate), // or toDMY(f.fromDate)
        toDate: toStr(f.toDate),     // or toDMY(f.toDate)

        month: month || "Select",
        year: year || "Select",

        page,
        pageSize,
      };

      const { data } = await axios.get(`${API_BASE}/list`, { params });
      if (!data?.success) throw new Error(data?.message || "List failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load progress details");
      setRows([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload list when page changes
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSearch = async () => {
    setPage(1);
    await loadList({});
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    const reset = {
      branch: filters.branch, // keep locked branch
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      month: "Select",
      year: "Select",
      fromDate: "",
      toDate: "",
    };
    setFilters((p) => ({ ...p, ...reset }));
    setPage(1);
    await loadList(reset);
    toast.info("Showing all records");
  };

  const handlePrint = () => {
    // ASPX opens ProgressDetailsPrint.aspx with session params.
    // In React, open your print route and pass filters via querystring (new tab).
    const month = !isSelect(filters.month) ? filters.month : (userCtx.prmonth || "Select");
    const year = !isSelect(filters.year) ? filters.year : (userCtx.pryear || "Select");

    const qs = new URLSearchParams({
      vehType: userCtx.vehType,
      branch: isSelect(filters.branch) ? "Select" : filters.branch,
      regNo: toStr(filters.regNo),
      vehicleCode: toStr(filters.vehicleCode),
      vehicleName: toStr(filters.vehicleName),
      fromDate: toStr(filters.fromDate),
      toDate: toStr(filters.toDate),
      month: toStr(month),
      year: toStr(year),
    }).toString();

    // ✅ Adjust route if your print page name differs
    window.open(`/vehicle/ProgressDetailsPrint?${qs}`, "_blank", "width=900,height=900,scrollbars=yes,resizable=yes");
  };

  const openStatusPopup = async (row) => {
  // status text (same as current)
  const status = row?.fld_status ?? row?.status ?? row?.Status ?? "";

  // ✅ try row vehicleId first
  let vehicleId = row?.vehicleId ?? row?.fld_vehicleid ?? row?.VehicleId ?? "";

  // ✅ branchId (try row first)
  let branchId = row?.branchId ?? row?.fld_branchid ?? row?.BranchId ?? "";

  // ✅ we must have vehicleCode to resolve like ASPX did using txt_search_vehiclecode.Text
  const vehicleCode =
    getCell(row, "fld_vehicle_code", "vehicleCode", "VehicleCode") ||
    toStr(filters.vehicleCode); // fallback to search textbox value

  const branchName = getCell(row, "fld_branchname", "BranchName") || "";

  // ✅ if vehicleId missing, resolve by calling backend route
  if (!vehicleId) {
    if (!vehicleCode) {
      toast.error("Vehicle Code missing — cannot resolve Vehicle ID");
      return;
    }

    try {
      const { data } = await axios.get(`${API_BASE}/getvehicleid`, {
        params: {
          vehicleCode,
          branchName,     // to resolve branchId like ASPX
          branchId,       // if already have, backend will keep it
        },
      });

      if (!data?.success) throw new Error(data?.message || "Failed to resolve vehicle id");

      vehicleId = data.vehicleId || "";
      branchId = data.branchId || branchId || "";
    } catch (err) {
      toast.error(err?.message || "Unable to fetch Vehicle ID");
      return;
    }
  }

  if (!vehicleId) {
    toast.error("Vehicle ID not found for this vehicle code");
    return;
  }

  // ✅ open ProgressStatus with resolved IDs
  const qs = new URLSearchParams({
    vehicleId: String(vehicleId),
    branchId: String(branchId || ""),
    status: String(status || ""),
    vehicleCode: String(vehicleCode || ""), // optional (handy in status page)
  }).toString();

  window.open(
    `/vehicle/ProgressStatus?${qs}`,
    "_blank",
    "width=650,height=450,top=100,left=100,resizable=no,scrollbars=yes,toolbar=no,status=no"
  );
};

  // Pagination UI
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  // Row helpers (map SP keys to UI columns)
  const getCell = (r, ...keys) => {
    for (const k of keys) {
      const v = r?.[k];
      if (v !== null && v !== undefined) return v;
    }
    return "";
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PROGRESS DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Progress Details
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/vehicle/ProgressMaster")} // ASPX: ibtn_addnew_Click1 -> ProgressMaster.aspx
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                title="Add New (Progress Master)"
              >
                + Add New
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`rounded-xl p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={lock.disableBranch}
                  >
                    {dd.branches.map((b) => (
                      <option key={`${b.value}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    list="regNoList"
                    type="text"
                    name="regNo"
                    value={filters.regNo}
                    onChange={handleChange}
                    onInput={async (e) => {
                      const q = e.target.value;
                      const items = await fetchSuggest("regNo", q);
                      setSuggest((p) => ({ ...p, regNo: items }));
                    }}
                    className={inputClass}
                    placeholder="Enter reg no"
                    disabled={lock.disableVehicleFields}
                  />
                  <datalist id="regNoList">
                    {suggest.regNo.map((x) => (
                      <option key={x} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    list="vehicleCodeList"
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    onInput={async (e) => {
                      const q = e.target.value;
                      const items = await fetchSuggest("vehicleCode", q);
                      setSuggest((p) => ({ ...p, vehicleCode: items }));
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={lock.disableVehicleFields}
                  />
                  <datalist id="vehicleCodeList">
                    {suggest.vehicleCode.map((x) => (
                      <option key={x} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    list="vehicleNameList"
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    onInput={async (e) => {
                      const q = e.target.value;
                      const items = await fetchSuggest("vehicleName", q);
                      setSuggest((p) => ({ ...p, vehicleName: items }));
                    }}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={lock.disableVehicleFields}
                  />
                  <datalist id="vehicleNameList">
                    {suggest.vehicleName.map((x) => (
                      <option key={x} value={x} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    {dd.months.map((m) => (
                      <option key={`${m.value}`} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    {dd.years.map((y) => (
                      <option key={`${y.value}`} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={inputClass} />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={inputClass} />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleViewAll}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  View All
                </button>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={handlePrint}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                  >
                    <FaPrint />
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your filters or click "View All".
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Branch</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Progress Date</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Location</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Status</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Starting KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Closing KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Total Working KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Cumulative KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Starting HR</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Closing HR</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Total Working HR</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Cumulative HR</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Production Qty</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Qty</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Cost</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Cons KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Cons HR</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Cost KM</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Diesel Cost HR</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const highlight =
                        Boolean(r?.isHighlight) ||
                        Boolean(r?.highlight) ||
                        String(r?.highlightFlag || "").toLowerCase() === "true" ||
                        String(r?.highlightFlag || "") === "1";

                      return (
                        <tr
                          key={idx}
                          className={`${
                            highlight
                              ? "bg-red-400/70"
                              : isDark
                              ? "bg-gray-800 hover:bg-gray-700"
                              : "bg-white hover:bg-gray-50"
                          } transition-colors duration-200`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_branchname", "BranchName")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_progressentrydate", "ProgressDate")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_Worklocationname", "WorkLocation")}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            <button
                              type="button"
                              onClick={() => openStatusPopup(r)}
                              className={`underline font-semibold ${
                                isDark ? "text-white hover:text-gray-200" : "text-blue-700 hover:text-blue-900"
                              }`}
                              title="View Status"
                            >
                              {getCell(r, "fld_status", "Status")}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_startingkilometer")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_closingkilometer")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_Workingkm", "km", "WorkingKm")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "cumKM", "CumulativeKm")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_statrtinghrs", "fld_startinghrs")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_closinghrs")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_Workinghr", "hrs", "WorkingHr")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "cumHR", "CumulativeHr")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_productionquantity")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_dieselquantity")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_fuelrate1")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_diselconsumptionkm")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_diselconsumptionhr")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_diselcostkm")}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {getCell(r, "fld_diselcosthr")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Page {page} of {totalPages} — Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of{" "}
                {total} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {pageButtons.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === p
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    page === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
